"use client";

import { useMemo, useRef, useState } from "react";
import { COUNTRIES, FAVOURITES, type Country } from "@aluume/shared/lib/countries";

// Searchable country combobox. Favourites (NZ/AU/US) pinned to the top. Used for the
// billing country (mode="name") and the phone dial code (mode="dial").
export function CountrySelect({
  value,
  onChange,
  mode = "name",
  autoComplete,
  name,
}: {
  value: string; // ISO2
  onChange: (c: Country) => void;
  mode?: "name" | "dial";
  /** When set, renders a hidden, correctly-tagged native <select> the browser can autofill. */
  autoComplete?: string;
  name?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const current = COUNTRIES.find((c) => c.iso === value);

  const list = useMemo(() => {
    const favs = FAVOURITES.map((iso) => COUNTRIES.find((c) => c.iso === iso)).filter(Boolean) as Country[];
    if (!q.trim()) return [...favs, ...COUNTRIES.filter((c) => !FAVOURITES.includes(c.iso))];
    const t = q.toLowerCase();
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(t) || c.dial.includes(t) || c.iso.toLowerCase() === t);
  }, [q]);

  function pick(c: Country) {
    onChange(c);
    setOpen(false);
    setQ("");
  }

  return (
    <div className="relative" ref={ref}>
      {/* Hidden native mirror so Google/browser autofill can populate the custom picker.
          name mode → autocomplete="country" (ISO values); dial mode → "tel-country-code" (dial digits). */}
      {autoComplete && (
        <select
          aria-hidden
          tabIndex={-1}
          name={name}
          autoComplete={autoComplete}
          value={mode === "dial" ? (current?.dial.replace("+", "") ?? "") : value}
          onChange={(e) => {
            const v = e.target.value;
            const c =
              mode === "dial"
                ? COUNTRIES.find((x) => x.dial.replace("+", "") === v)
                : COUNTRIES.find((x) => x.iso === v);
            if (c) onChange(c);
          }}
          className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
        >
          {mode === "dial"
            ? Array.from(new Set(COUNTRIES.map((c) => c.dial.replace("+", "")))).map((d) => (
                <option key={d} value={d}>+{d}</option>
              ))
            : COUNTRIES.map((c) => (
                <option key={c.iso} value={c.iso}>{c.name}</option>
              ))}
        </select>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center border-b border-ink/30 py-2 text-left text-[15px] text-ink ${mode === "dial" ? "gap-1" : "gap-2"}`}
      >
        {current ? (
          <span className="whitespace-nowrap">{current.flag} {mode === "dial" ? current.dial : current.name}</span>
        ) : (
          <span className="text-ink-soft">Select…</span>
        )}
        {/* dial mode hugs the caret next to the code (minimal width); name mode pushes it to the far edge */}
        <span className={`text-[10px] text-ink-soft ${mode === "dial" ? "ml-0.5" : "ml-auto"}`}>▾</span>
      </button>
      {open && (
        <div className="absolute left-0 top-12 z-50 max-h-72 w-72 overflow-auto rounded-lg bg-white p-2 shadow-[0px_18px_40px_-12px_rgba(57,51,49,0.25)]">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search country…"
            className="mb-2 w-full border-b border-ink/20 bg-transparent px-2 py-1.5 text-[14px] outline-none"
          />
          {!q.trim() && (
            <p className="px-2 py-1 text-[10px] uppercase tracking-[1px] text-ink-soft">Favourites</p>
          )}
          {list.map((c, i) => {
            const showDivider = !q.trim() && i === FAVOURITES.length;
            return (
              <span key={c.iso}>
                {showDivider && <p className="mt-1 border-t border-ink/10 px-2 pb-1 pt-2 text-[10px] uppercase tracking-[1px] text-ink-soft">All countries</p>}
                <button
                  type="button"
                  onClick={() => pick(c)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[14px] ${c.iso === value ? "bg-cream-deep" : "hover:bg-cream-warm"}`}
                >
                  <span>{c.flag}</span>
                  <span className="text-ink">{c.name}</span>
                  <span className="ml-auto text-ink-soft">{c.dial}</span>
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
